import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { api, type Acceptance, type Lookup } from '../lib/api'
import { Panel, Field, Alert, Loading, Tag } from '../components/ui'

/**
 * 編輯已建立的驗收單。
 *
 * 只開放不影響金額與篇數的欄位——驗收人、日期、請款月份、備註、品質檢核。
 * 「單篇結果」不在這裡改：那會牽動已驗篇數、發包單狀態與請款列，
 * 要改結果請用「作廢此驗收單」再重建，那條路本來就會完整反向沖銷。
 */
export default function AcceptanceEdit() {
  const { id } = useParams()
  const nav = useNavigate()
  const [a, setA] = useState<Acceptance | null>(null)
  const [lookup, setLookup] = useState<Lookup | null>(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    reviewer_id: null as number | null,
    delivered_on: '',
    reviewed_on: '',
    billing_period: '',
    remark: '',
  })
  const [checks, setChecks] = useState<
    Array<{ code: string; label: string; verdict: '符合' | '不符'; note: string }>
  >([])

  useEffect(() => {
    api.get<Lookup>('/masters/lookup').then(setLookup).catch(() => {})
  }, [])

  useEffect(() => {
    api
      .get<Acceptance>(`/acceptances/${id}`)
      .then((res) => {
        setA(res)
        const r = res as any
        setForm({
          reviewer_id: r.reviewer_id ?? null,
          delivered_on: r.delivered_on ?? '',
          reviewed_on: r.reviewed_on ?? '',
          // 舊資料沒有 billing_period，退回驗收單自己的月份，跟後端的判斷一致
          billing_period: r.billing_period || r.period || '',
          remark: r.remark ?? '',
        })
        setChecks(
          (r.checks ?? []).map((c: any) => ({
            code: c.code,
            label: c.label ?? c.code,
            verdict: c.verdict === '不符' ? '不符' : '符合',
            note: c.note ?? '',
          }))
        )
      })
      .catch((e) => setError(e.message))
  }, [id])

  const save = async () => {
    setError('')
    setSaving(true)
    try {
      await api.put(`/acceptances/${id}`, {
        ...form,
        checks: checks.map((c) => ({ code: c.code, verdict: c.verdict, note: c.note })),
      })
      alert('已儲存')
      nav(`/acceptances/${id}/print`)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  if (error && !a) return <Alert kind="error">{error}</Alert>
  if (!a) return <Loading />

  const voided = Boolean((a as any).voided_at)

  return (
    <>
      <div className="page-head">
        <h2>編輯驗收單 {a.code}</h2>
        {voided && <Tag value="已作廢" />}
        <div className="spacer" />
        <div className="btn-row">
          <Link to={`/acceptances/${id}/print`}><button>取消</button></Link>
          <button className="primary" onClick={save} disabled={saving || voided}>
            {saving ? '儲存中…' : '儲存變更'}
          </button>
        </div>
      </div>

      <Alert kind="error">{error}</Alert>

      {voided ? (
        <Alert kind="warn">這張驗收單已作廢，不能編輯。</Alert>
      ) : (
        <>
          <Panel title="一、基本資料">
            <div className="grid c12">
              <Field label="驗收人" span={3}>
                <select
                  value={form.reviewer_id ?? ''}
                  onChange={(e) => setForm({ ...form, reviewer_id: Number(e.target.value) || null })}
                >
                  <option value="">— 未指定 —</option>
                  {lookup?.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="實際交付日" span={3}>
                <input
                  type="date"
                  value={form.delivered_on}
                  onChange={(e) => setForm({ ...form, delivered_on: e.target.value })}
                />
              </Field>
              <Field label="驗收日期" span={3}>
                <input
                  type="date"
                  value={form.reviewed_on}
                  onChange={(e) => setForm({ ...form, reviewed_on: e.target.value })}
                />
              </Field>
              <Field label="請款月份" span={3}>
                <input
                  type="month"
                  value={form.billing_period}
                  onChange={(e) => setForm({ ...form, billing_period: e.target.value })}
                />
              </Field>
              <Field label="驗收備註／退件與修改紀錄" span={12}>
                <textarea value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
              </Field>
            </div>
            <p className="hint" style={{ margin: '10px 0 0', fontSize: 12 }}>
              改請款月份會一併更新這張單底下「待請款」的請款列；已請款的不會動，也不允許改。
              篇數、單價、單篇結果不能在這裡改 —— 要改請用列印頁的「作廢此驗收單」再重建。
            </p>
          </Panel>

          <Panel title="二、品質檢核">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>編號</th>
                  <th>檢查項目</th>
                  <th style={{ width: 96 }}>判定</th>
                  <th style={{ minWidth: 160 }}>備註／缺失說明</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((c, i) => (
                  <tr key={c.code} style={c.verdict === '不符' ? { background: '#fdf6f5' } : undefined}>
                    <td>{c.code}</td>
                    <td>{c.label}</td>
                    <td>
                      <select
                        value={c.verdict}
                        onChange={(e) =>
                          setChecks((list) =>
                            list.map((x, k) => (k === i ? { ...x, verdict: e.target.value as '符合' | '不符' } : x))
                          )
                        }
                      >
                        <option value="符合">符合</option>
                        <option value="不符">不符</option>
                      </select>
                    </td>
                    <td>
                      {/* 判定符合也能寫字：常有「已修正後通過」這種要留下來的說明 */}
                      <input
                        value={c.note}
                        placeholder={c.verdict === '符合' ? '選填' : '請說明缺失'}
                        onChange={(e) =>
                          setChecks((list) => list.map((x, k) => (k === i ? { ...x, note: e.target.value } : x)))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </>
  )
}
